import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import FormularioRepresentante from './FormularioRepresentante';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PageButtonSimple from '../GeneralComponents/PageButtonSimple';
import PageButtonBlack from '../GeneralComponents/PageButtonBlack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ModalStandardCancel from "./ModalStandardCancel";
import axios from "../../api/axios";

// Generador de IDs únicos
const generarId = () => crypto.randomUUID();

const PanelAdminRepresentantes = forwardRef(({ initialData = [], rutUsuario, soloLectura= false, mineduc=false }, ref) => {
  const itemRefs = useRef([]);

  const [open, setOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalMsg, setModalMsg] = useState("");
  const [modalErrorFlag, setModalErrorFlag] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [failedReps, setFailedReps] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Prepara objetos con id, isNew e isModified
  const preparar = (data, marcadoNuevo = false) =>
    data.map(d => ({
      id: d.id ?? generarId(),
      nombre: d.nombre || '',
      apellidos: d.apellidos || '',
      rut: d.rut || '',
      email: d.email || '',
      telefono: d.telefono || '',
      rbd: d.rbd || '',
      isNew: marcadoNuevo,
      isModified: false
    }));

  // Ref para guardar copia inmutable de datos originales
  const originalRepsRef = useRef(
    preparar(initialData, false).map(d => ({ ...d }))
  );

  const [representantes, setRepresentantes] = useState(
    initialData.length
      ? preparar(initialData, false)
      : [preparar([{}], true)[0]]
  );
  const [errores, setErrores] = useState(
    representantes.map(() => ({ rut: false, email: false, telefono: false }))
  );

const disableActions = React.useMemo(() => {
  if (mineduc) return false;
  const exists = representantes.some(r => r.rut === rutUsuario);
  return representantes.length > 0 && !exists;
}, [representantes, rutUsuario, mineduc]);


  useEffect(() => {
    if (initialData.length) {
      const prepared = preparar(initialData, false);
      setRepresentantes(prepared);
      setErrores(initialData.map(() => ({ rut: false, email: false, telefono: false })));
      setFailedReps([]);
      itemRefs.current = [];
      setModalErrorFlag(false);
      originalRepsRef.current = prepared.map(d => ({ ...d }));
    }
  }, [initialData]);


  // Maneja cambios en campos con validaciones y controla isModified
  const handleFormChange = (index, name, value, forceError = false) => {
    const rep = representantes[index];
    const canEditRutNombre = rep.isNew;
    const canEditContacto = rep.isNew || rep.rut === rutUsuario;

    if ((name === 'rut' || name === 'nombre') && !canEditRutNombre) return;
    if ((name === 'email' ) && !canEditContacto) return;

    // Clonamos array y objeto para no mutar referencias previas
    const nuevos = representantes.map((r, i) =>
      i === index ? { ...r, [name]: value } : r
    );

    // Si era un registro existente y cambia un campo, lo marcamos como modificado
    if (!rep.isNew) {
      const original = originalRepsRef.current[index] || {};
      const campos = ['nombre', 'rut', 'email', 'telefono', 'apellidos'];
      const modificado = campos.some(
        c => nuevos[index][c] !== original[c]
      );
      nuevos[index].isModified = modificado;
    }

    setRepresentantes(nuevos);

    // Validaciones de errores
    let nuevosErr = [...errores];
    if (name === 'rut') {
      nuevosErr[index].rut = forceError;
      const all = nuevos.map(r => r.rut.trim()).filter(r => r);
      const seen = new Set();
      const duplicates = new Set();
      all.forEach(r => {
        if (seen.has(r)) duplicates.add(r);
        else seen.add(r);
      });
      nuevosErr = nuevosErr.map((err, i) => {
        const rutVal = nuevos[i].rut.trim();
        if (duplicates.has(rutVal)) {
          const firstIndex = nuevos.findIndex(
            r => r.rut.trim() === rutVal
          );
          if (i !== firstIndex) return { ...err, rut: true };
        }
        return err;
      });
    } else if (name === 'email') {
      nuevosErr[index].email = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    } 
    setErrores(nuevosErr);
  };



  const modalOpen = (idx) => {
    if (disableActions) return;
    setCurrentIndex(idx);
    setModalErrorFlag(false);
    setModalContent("Deshabilitar usuario");
    setModalMsg(
      `¿Está seguro deshabilitar al Representante ${representantes[idx].nombre} ${representantes[idx].apellidos}?`
    );
    if (!representantes[idx].isNew) handleOpen();
    else eliminarRepresentante(idx);
  };

  const removeRepresentante = () => {
    const info = representantes[currentIndex];
    axios.deleteRepresentante({
      rbd: info.rbd,
      nombre_representante: info.nombre,
      rut_representante: info.rut
    })
      .then(() => eliminarRepresentante(currentIndex))
      .catch(() => setModalErrorFlag(true))
      .finally(() => setOpen(false));
  };

  const eliminarRepresentante = (index) => {
    if (index === 0) return;
    setRepresentantes(prev => prev.filter((_, i) => i !== index));
    setErrores(prev => prev.filter((_, i) => i !== index));
  };

  const validarTodos = () => {
    for (let i = 0; i < representantes.length; i++) {
      const rep = representantes[i];
      const err = errores[i];
      const canEditRutNombre = rep.isNew;
      const canEditContacto = rep.isNew || rep.rut === rutUsuario;

      if (
        canEditRutNombre &&
        (!rep.nombre || !rep.rut || err.rut)
      ) {
        itemRefs.current[i]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        return false;
      }
      if (
        canEditContacto &&
        (!rep.email || err.email)
      ) {
        itemRefs.current[i]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        return false;
      }
    }
    return true;
  };

  const handleEnviar = async (rbdParam) => {
    if (!validarTodos()) return [];
    // Enviamos nuevos y modificados
    const aEnviar = representantes.filter(
      r => r.isNew || r.isModified
    );
    if (!aEnviar.length) return [];
    const fallidos = [];
    for (const rep of aEnviar) {
      try {
        if (rep.isNew || rep.isModified) {
          await axios.addRepresentante({
            rbd: rbdParam,
            nombre_representante: rep.nombre,
            rut_representante: rep.rut,
            apellido_representante: rep.apellidos,
            email_representante: rep.email,
            telefono: rep.telefono
          });
        }
        // Reiniciamos flags
        setRepresentantes(prev => prev.map(r =>
          r.id === rep.id
            ? { ...r, isNew: false, isModified: false }
            : r
        ));
      } catch {
        fallidos.push(rep);
      }
    }
    setFailedReps(fallidos);
    return fallidos;
  };

  useImperativeHandle(ref, () => ({
    enviarFormulario: handleEnviar,
    esValido: validarTodos,
    datos: representantes,
    setDatosExternos: (nuevos) => {
      const prepar = preparar(nuevos, false);
      setRepresentantes(prepar);
      setErrores(nuevos.map(() => ({ rut: false, email: false, telefono: false })));
      setFailedReps([]);
      itemRefs.current = [];
      // Actualizamos copia original con clones
      originalRepsRef.current = prepar.map(d => ({ ...d }));
    },

    agregarRepresentante : () => {
      if (disableActions) return;
      const nuevo = preparar([{}], true)[0];
      setRepresentantes(prev => [...prev, nuevo]);
      setErrores(prev => [...prev, { rut: false, email: false, telefono: false }]);
    }
  }));

  return (
    <Box>
      {representantes.map((rep, idx) => (
        <Box
          key={rep.id}
          ref={el => itemRefs.current[idx] = el}
          sx={{ mb: 3, display: 'flex', alignItems: 'stretch' }}
        >
          {((representantes.length > 1 && idx !== 0) && !mineduc) && (
            <IconButton
              onClick={() => modalOpen(idx)}
              disabled={disableActions || representantes[idx].rut === rutUsuario}
              size="small"
              sx={{
                width: 36,
                backgroundColor: 'rgba(255,71,49,0.1)',
                color: '#FF4731',
                borderRadius: 2,
                flexShrink: 0,
              }}
              aria-label="Eliminar"
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          )}
          <Box sx={{ flex: 1, ml: (representantes.length > 1 && idx !== 0) && !mineduc ? 3 : 0 }}>
            <FormularioRepresentante
              index={idx}
              formData={rep}
              errors={errores[idx]}
              editableRutNombre={rep.isNew}
              editableContacto={rep.isNew || rep.rut === rutUsuario}
              onChange={handleFormChange}
            />
            {rep.isNew && (
              <Typography variant="caption" color="primary">
                Nuevo
              </Typography>
            )}
            {rep.isModified && !rep.isNew && (
              <Typography variant="caption" color="warning.main">
                Modificado
              </Typography>
            )}
          </Box>
        </Box>
      ))}

     

      <ModalStandardCancel
        open={open}
        error={true}
        title={modalContent}
        msg="Si deshabilita al Representante esta acción solo podrá ser revertida por MINEDUC"
        qst={modalMsg}
        btnText="Ok, deshabilitar el representante"
        onClick={removeRepresentante}
        onClose={handleClose}
      />
    </Box>
  );
});

export default PanelAdminRepresentantes;
